import { format } from 'date-fns';

import { API_URL } from '../config/api';
import { auditFollowUpPdfPath, auditPdfPath } from '../config/auditPdfEndpoints';
import apiClient from '../store/apiClient';
import HttpError from './providerHelpers/HttpError';

export const isFollowUpAudit = audit => {
  if (!audit) return false;
  if (audit.auditType === 'AuditFollowUp') return true;
  if (audit.auditId && audit.id && audit.auditId !== audit.id) return true;
  return false;
};

const parseFilenameFromContentDisposition = header => {
  if (!header || typeof header !== 'string') return null;
  const encoded = /filename\*=(?:UTF-8'')?([^;\n]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].replace(/^"+|"+$/g, '').trim());
    } catch (e) {
      return encoded[1].replace(/^"+|"+$/g, '').trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];
  const plain = /filename=([^;\n]+)/i.exec(header);
  if (plain) return plain[1].replace(/^"+|"+$/g, '').trim();
  return null;
};

const saveBlobAsFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Same header as other file downloads in {@link ./providerHelpers/fetchUtils}. */
const resolveDownloadFilename = (response, audit) => {
  const fileNameHeader = response.headers.get('file-name');
  if (fileNameHeader) {
    const fromFileNameHeader = decodeURI(fileNameHeader)?.replaceAll(
      '{datetime}',
      format(new Date(), 'MM-dd-yyyy-HH-mm-ss'),
    );
    if (fromFileNameHeader) return fromFileNameHeader;
  }

  const fromContentDisposition = parseFilenameFromContentDisposition(
    response.headers.get('Content-Disposition'),
  );
  if (fromContentDisposition) return fromContentDisposition;

  return isFollowUpAudit(audit) ? `audit-follow-up-${audit.id}.pdf` : `audit-${audit.id}.pdf`;
};

/**
 * Downloads the stored PDF for a completed audit or follow-up row.
 * @throws {HttpError}
 */
export const downloadStoredPsmAuditPdf = async audit => {
  const relativePath = isFollowUpAudit(audit)
    ? auditFollowUpPdfPath(audit.id)
    : auditPdfPath(audit.id);
  const url = `${API_URL.replace(/\/$/, '')}/${relativePath}`;

  const response = await apiClient(
    url,
    {
      method: 'GET',
      headers: new Headers({ Accept: 'application/pdf' }),
    },
    false,
  );

  const blob = await response.blob();
  if (!blob || blob.size === 0) {
    throw new HttpError('PDF file is empty or not available.', 404);
  }

  const filename = resolveDownloadFilename(response, audit);
  saveBlobAsFile(blob, filename);
};
